'use client';

import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { Badge, type BadgeProps as BaseBadgeProps } from '../../primitives/badge';
import { cn } from '../../utils/cn';

export interface UzBadgeProps extends Omit<HTMLAttributes<HTMLDivElement>, 'color'> {
  /** Semantic tone. Maps to the shadcn variant under the hood. */
  tone?: 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive';
  /** Show a leading dot. Useful for status indicators. */
  dot?: boolean;
  /** Small leading icon. */
  icon?: ReactNode;
  /** Pill-shape (default true). Set false for rounded square. */
  pill?: boolean;
}

const toneToVariant: Record<NonNullable<UzBadgeProps['tone']>, BaseBadgeProps['variant']> = {
  default: 'default',
  secondary: 'secondary',
  outline: 'outline',
  success: 'success',
  warning: 'warning',
  destructive: 'destructive',
};

export const UzBadge = forwardRef<HTMLDivElement, UzBadgeProps>(
  ({ tone = 'default', dot, icon, pill = true, className, children, ...rest }, ref) => (
    <Badge
      ref={ref}
      variant={toneToVariant[tone]}
      className={cn(!pill && 'rounded-md', 'gap-1.5', className)}
      {...rest}
    >
      {dot && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />}
      {icon}
      {children}
    </Badge>
  ),
);
UzBadge.displayName = 'UzBadge';
